import React from "react"
import { Repeater, types } from "react-bricks/frontend"
import {
    Background,
    BackgroundColor,
    backgroundGreyScaleColorsOptions,
    Container,
    ContainerType,
    Spacer,
    spacerOptionsAll,
    SpacerVariant,
} from "../../components/layout"
import Image, { AspectRatio } from "../../Image"
import Typography, { TypographyElement } from "../../Typography"
import classNames from "classnames"
import { Color } from "../../components/typography"

interface TextImageBlockProps {
    background: { color: BackgroundColor } | BackgroundColor
    reversePosition: boolean
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
    text: TypographyElement[]
}

const TextImageBlock: types.Brick<TextImageBlockProps> = ({ background, reversePosition, spacerTop, spacerBottom }) => {
    const bgColor = typeof background === "object" ? background.color : background
    return (
        <Background color={bgColor}>
            <Spacer variant={spacerTop} />
            <Container type={ContainerType.Article}>
                <div className="flex flex-col sm:flex-row w-full gap-y-6 sm:gap-x-10 lg:gap-x-20">
                    <div
                        className={classNames("max-w-40 sm:max-w-[200px] lg:max-w-[300px] w-fit shrink-0", {
                            "order-1": !reversePosition,
                            "sm:order-2": reversePosition,
                        })}
                    >
                        <Image className="rounded-base lg:rounded-lg" aspectRatio={AspectRatio["1/1"]} maxWidth={300} />
                    </div>
                    <div
                        className={classNames("flex flex-col place-content-center", {
                            "order-2": !reversePosition,
                            "sm:order-1": reversePosition,
                        })}
                    >
                        <Color>
                            <Typography
                                block="copytext-article"
                                allowList={[
                                    "h4",
                                    "h5",
                                    "articleUl",
                                    "small",
                                    "sup",
                                    "link",
                                    "bold",
                                    "italic",
                                    "quote",
                                    "checkList",
                                ]}
                            />
                        </Color>
                        <Repeater
                            propName="repeater"
                            renderWrapper={children => (
                                <div className="flex flex-col space-y-5 sm:space-y-7">{children}</div>
                            )}
                        />
                    </div>
                </div>
            </Container>
            <Spacer variant={spacerBottom} />
        </Background>
    )
}

TextImageBlock.schema = {
    name: "TextImageBlock",
    label: "Text Image Block",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/TextImageBlock.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Button",
                    label: "Button",
                    max: 1,
                },
                {
                    type: "TextLink",
                    label: "Text Link",
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "reversePosition",
            label: "Reverse Position",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                display: types.OptionsDisplay.Color,
                options: backgroundGreyScaleColorsOptions,
            },
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): TextImageBlockProps => ({
        background: { color: BackgroundColor.White },
        reversePosition: false,
        text: [
            {
                type: "h4",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                    },
                ],
            },
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc placerat sagittis faucibus.",
                    },
                ],
            },
        ],
        spacerTop: SpacerVariant.XS,
        spacerBottom: SpacerVariant.XS,
    }),
}

export default TextImageBlock
